<template>
  <section class="configure-phase-rules">
    <div class="header">
      <h2>Regras de horas extras (fases)</h2>
      <p>
        Defina, para cada tipo de dia, a duração de cada fase de hora extra e se
        elas devem entrar no saldo. Selecione os turnos abaixo e aplique em massa.
      </p>
      <p class="company-link-note">
        Turnos que usam a <strong>configuração da empresa</strong> têm os percentuais
        controlados no nível da empresa — alterações aqui podem ser sobrescritas.
        <a href="javascript:void(0)" @click="goToCompanyPhaseRules">
          Editar regras padrão da empresa &rarr;
        </a>
      </p>
    </div>

    <div v-if="loading" class="loading-block">Carregando turnos...</div>

    <div v-if="!loading">
      <!-- Seleção de turnos -->
      <div class="box">
        <label class="box-title">
          Turnos da empresa
          <span class="select-all">
            <input type="checkbox" :checked="allSelected" @change="toggleAll($event.target.checked)" />
            Selecionar todos<span v-if="workshiftFilter.trim()"> (filtrados)</span>
          </span>
        </label>
        <div class="workshift-search">
          <input type="text" v-model="workshiftFilter" placeholder="Buscar turno pelo nome..." />
          <span class="count">
            {{ filteredWorkshifts.length }} de {{ workshifts.length }} turno(s)
            <template v-if="selectedWorkshifts.length"> · {{ selectedWorkshifts.length }} selecionado(s)</template>
          </span>
        </div>
        <table class="table-workshifts">
          <thead>
            <tr>
              <th></th>
              <th>{{ labels.workShift.name }}</th>
              <th>{{ labels.workShift.workShiftType }}</th>
              <th>Config.</th>
              <th>{{ labels.workShift.extraordinaryPhase }}</th>
              <th>{{ labels.workShift.timeZone }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ws in filteredWorkshifts" :key="ws.id">
              <td><input type="checkbox" :value="ws.id" v-model="selectedWorkshifts" /></td>
              <td>{{ ws.name }}</td>
              <td>{{ helpers.getWorkShiftTypeLabel(ws.workShiftType) }}</td>
              <td>
                <span v-if="ws.usingSameCompanySettingsConfig" class="badge-company"
                      title="Este turno segue a configuração da empresa: o percentual das fases é controlado no nível da empresa.">
                  Segue empresa
                </span>
                <span v-else class="badge-own" title="Turno com configuração própria.">Própria</span>
              </td>
              <td>{{ helpers.getExtraordinaryPhaseLabel(ws.extraordinaryPhase) }}</td>
              <td>{{ helpers.formatInheritableTimezoneFriendly(ws.timeZone, companyTimeZone, timezones) }}</td>
            </tr>
            <tr v-if="!filteredWorkshifts.length">
              <td colspan="6" class="empty-row">Nenhum turno encontrado para "{{ workshiftFilter }}".</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Template: carregar regras de um turno existente -->
      <div class="box template-box">
        <label class="box-title">
          Carregar regras de um turno existente
          <span class="hint" title="Útil pra evitar digitar tudo. Selecione um turno como modelo, os campos abaixo serão preenchidos com as fases atuais dele, depois você ajusta e aplica em massa nos turnos selecionados acima.">
            ?
          </span>
        </label>
        <p class="hint-line">
          Preenche o formulário abaixo com as fases <strong>atuais</strong> do
          turno escolhido. Você pode editar antes de aplicar.
        </p>
        <div class="template-row">
          <input type="text" v-model="templateFilter" class="template-search"
                 placeholder="Buscar turno pelo nome..." />
          <select v-model="templateWorkshiftId" class="template-select">
            <option value="">Selecione um turno modelo{{ templateFilter.trim() ? ' (' + filteredTemplateWorkshifts.length + ')' : '' }}</option>
            <option v-for="ws in filteredTemplateWorkshifts" :key="ws.id" :value="ws.id">{{ ws.name }} ({{ helpers.getWorkShiftTypeLabel(ws.workShiftType) }})</option>
          </select>
          <button class="btn-secondary"
                  :disabled="!templateWorkshiftId || loadingTemplate"
                  @click="loadTemplateFromWorkshift">
            <span v-if="!loadingTemplate">Carregar regras desse turno</span>
            <span v-else>Carregando...</span>
          </button>
          <button class="btn-link" @click="resetRules" title="Limpa o formulário e volta para os valores padrão.">
            Limpar
          </button>
        </div>
      </div>

      <!-- Percentuais das fases (nível do turno) -->
      <div class="box">
        <label class="box-title">Percentuais das fases de hora extra (do turno)</label>
        <p class="hint-line">
          O percentual é único por turno (aplicado a todos os tipos de dia). Marque
          abaixo para também atualizar os percentuais nos turnos selecionados ao aplicar.
          Valores em número inteiro (ex.: 50 = +50%).
        </p>
        <div class="form-row checkbox-line">
          <label>
            <input type="checkbox" v-model="form.applyPhasePercentages" />
            Atualizar os percentuais das fases nos turnos selecionados
          </label>
        </div>
        <div v-if="form.applyPhasePercentages && selectedSameConfigWorkshifts.length" class="same-config-warning">
          <strong>Atenção:</strong> {{ selectedSameConfigWorkshifts.length }} turno(s) selecionado(s)
          seguem a <strong>configuração da empresa</strong> e <u>não</u> receberão o percentual
          individual (o sistema ignora o % nesses turnos para não gerar saldo indevido). Para
          aplicar um percentual nesses turnos, desmarque a opção <em>"usar a mesma configuração
          da empresa"</em> no turno, ou ajuste na
          <a href="javascript:void(0)" @click="goToCompanyPhaseRules">tela de regras da empresa</a>.
          <div class="same-config-list">{{ selectedSameConfigWorkshifts.map(w => w.name).join(', ') }}</div>
        </div>
        <div class="percent-row" v-if="form.applyPhasePercentages">
          <div class="percent-field">
            <label>% 1ª fase</label>
            <input type="number" min="0" step="1" v-model.number="form.firstPeriodTimePercent" placeholder="Ex: 50" />
          </div>
          <div class="percent-field">
            <label>% 2ª fase</label>
            <input type="number" min="0" step="1" v-model.number="form.secondPeriodTimePercent" placeholder="Ex: 60" />
          </div>
          <div class="percent-field">
            <label>% 3ª fase</label>
            <input type="number" min="0" step="1" v-model.number="form.thirdPeriodTimePercent" placeholder="Ex: 100" />
          </div>
        </div>
      </div>

      <!-- Regras de fases -->
      <div class="box">
        <label class="box-title">Regras (uma linha por tipo de dia)</label>
        <p class="hint-line">
          Cada linha representa um tipo de dia. Ative só os dias que você quer
          aplicar — dias com "Fase ativa" desmarcada serão ignorados ao salvar
          se o turno alvo não tiver fase pra esse dia.
        </p>
        <table class="table-rules">
          <thead>
            <tr>
              <th title="Dia da semana, feriado ou folga ao qual essa regra se aplica.">
                {{ labels.workshiftDayPhase.workshiftDayPhaseType }}
              </th>
              <th title="Quando desmarcado, o sistema trata esse dia como sem fase extraordinária ativa (zera durações e saldos para o tipo).">
                {{ labels.workshiftDayPhase.isActive }}
              </th>
              <th title="Tempo máximo que conta como 1ª fase de hora extra (formato HH:MM:SS). Após esse limite, a hora migra para a 2ª fase.">
                {{ labels.workshiftDayPhase.firstPhaseDuration }}
              </th>
              <th title="Marque se as horas da 1ª fase devem somar no saldo do banco de horas. Se desmarcado, viram extras pagas/ignoradas conforme remuneração.">
                {{ labels.workshiftDayPhase.applyFirstPercentageToBalance }}
              </th>
              <th title="Tempo máximo que conta como 2ª fase (após a 1ª). Acima desse limite, a hora vira 3ª fase.">
                {{ labels.workshiftDayPhase.secondPhaseDuration }}
              </th>
              <th title="Marque se as horas da 2ª fase devem somar no saldo do banco de horas.">
                {{ labels.workshiftDayPhase.applySecondPercentageToBalance }}
              </th>
              <th title="Tempo da 3ª fase (geralmente o teto de HE3 noturna / domingo / feriado). 00:00:00 = sem limite.">
                {{ labels.workshiftDayPhase.thirdPhaseDuration }}
              </th>
              <th title="Marque se as horas da 3ª fase devem somar no saldo do banco de horas.">
                {{ labels.workshiftDayPhase.applyThirdPercentageToBalance }}
              </th>
              <th title="Quando marcado, o tempo de intervalo (almoço/descanso) entra no cálculo de saldo positivo. Use com cuidado.">
                {{ labels.workshiftDayPhase.addIntervalToTotalPlus }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rule in rules" :key="rule.workshiftDayPhaseType">
              <td>{{ helpers.getPhaseTypeLabel(rule.workshiftDayPhaseType) }}</td>
              <td><input type="checkbox" v-model="rule.isActive" /></td>
              <td><input type="text" v-model="rule.firstPhaseDuration" placeholder="00:00:00" title="HH:MM:SS"/></td>
              <td><input type="checkbox" v-model="rule.applyFirstPercentageToBalance" /></td>
              <td><input type="text" v-model="rule.secondPhaseDuration" placeholder="00:00:00" title="HH:MM:SS"/></td>
              <td><input type="checkbox" v-model="rule.applySecondPercentageToBalance" /></td>
              <td><input type="text" v-model="rule.thirdPhaseDuration" placeholder="00:00:00" title="HH:MM:SS"/></td>
              <td><input type="checkbox" v-model="rule.applyThirdPercentageToBalance" /></td>
              <td><input type="checkbox" v-model="rule.addIntervalToTotalPlus" /></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="actions">
        <button class="btn-cancel" @click="$router.go(-1)">Voltar</button>
        <button class="btn-primary" :disabled="!canSave || saving" @click="save()">
          <span v-if="!saving">Aplicar em {{ selectedWorkshifts.length }} turno(s)</span>
          <span v-else>Aplicando...</span>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import {
  GET_WORKSHIFT_FULL_VIEW,
  APPLY_PHASE_RULES_BULK,
  GET_WORKSHIFT_PHASES,
  GET_TIMEZONE_LIST
} from "../../../../urlConfig";
import { REQUEST_SERVICE } from "../../../../requestConfig";
import {
  workShiftLabels,
  workshiftDayPhaseLabels,
  workshiftDayPhaseTypeLabels,
  labelHelpers
} from "../../../../labels/workshiftLabels";
import swal from "sweetalert";

// Tipos de dia que aparecem nas regras (mais comuns).
const DEFAULT_PHASE_TYPES = [1, 2, 3, 4, 5, 6, 0, 7, 8]; // seg..sex, sab, dom, feriado, folga

function emptyRule(type) {
  return {
    workshiftDayPhaseType: type,
    isActive: false,
    firstPhaseDuration: "00:00:00",
    secondPhaseDuration: "00:00:00",
    thirdPhaseDuration: "00:00:00",
    applyFirstPercentageToBalance: true,
    applySecondPercentageToBalance: true,
    applyThirdPercentageToBalance: true,
    addIntervalToTotalPlus: false
  };
}

// Converte string TimeSpan vinda do backend ("HH:MM:SS" ou ".NET 'HH:mm:ss'")
// para o mesmo formato usado nos inputs. Aceita objetos TimeSpan serializados.
function normalizeTimeSpan(value) {
  if (!value) return "00:00:00";
  if (typeof value !== "string") return String(value);
  // Caso venha "HH:MM:SS.fff" (com milisegundos), corta o ms.
  const cleaned = value.split(".")[0];
  // Garante 3 segmentos
  const parts = cleaned.split(":");
  while (parts.length < 3) parts.push("00");
  return parts.slice(0, 3).map((p) => p.padStart(2, "0")).join(":");
}

export default {
  name: "configurePhaseRules",
  data() {
    return {
      loading: false,
      saving: false,
      companyId: "",
      companyTimeZone: "",
      timezones: {},
      workshifts: [],
      selectedWorkshifts: [],
      // Filtros por nome (empresas com muitos turnos)
      workshiftFilter: "",
      templateFilter: "",
      rules: DEFAULT_PHASE_TYPES.map(emptyRule),
      templateWorkshiftId: "",
      loadingTemplate: false,
      // Percentuais das fases — nível do turno (um valor por turno).
      form: {
        applyPhasePercentages: false,
        firstPeriodTimePercent: 0,
        secondPeriodTimePercent: 0,
        thirdPeriodTimePercent: 0
      },
      labels: {
        workShift: workShiftLabels,
        workshiftDayPhase: workshiftDayPhaseLabels,
        phaseType: workshiftDayPhaseTypeLabels
      },
      helpers: labelHelpers
    };
  },
  computed: {
    // Turnos filtrados por nome para a TABELA de seleção.
    filteredWorkshifts() {
      const q = (this.workshiftFilter || "").trim().toLowerCase();
      if (!q) return this.workshifts;
      return this.workshifts.filter((w) => (w.name || "").toLowerCase().indexOf(q) !== -1);
    },
    // Turnos filtrados por nome para o DROPDOWN de turno modelo.
    filteredTemplateWorkshifts() {
      const q = (this.templateFilter || "").trim().toLowerCase();
      if (!q) return this.workshifts;
      return this.workshifts.filter((w) => (w.name || "").toLowerCase().indexOf(q) !== -1);
    },
    // "Selecionar todos" passa a considerar a lista FILTRADA (marca/desmarca só o que está visível).
    allSelected() {
      const list = this.filteredWorkshifts;
      if (!list.length) return false;
      return list.every((w) => this.selectedWorkshifts.indexOf(w.id) !== -1);
    },
    // Turnos SELECIONADOS que seguem a configuração da empresa: nesses o percentual
    // não é aplicado (o backend pula), então avisamos o usuário.
    selectedSameConfigWorkshifts() {
      return this.workshifts.filter(
        (w) => w.usingSameCompanySettingsConfig && this.selectedWorkshifts.indexOf(w.id) !== -1
      );
    },
    canSave() {
      return this.selectedWorkshifts.length > 0;
    }
  },
  beforeMount() {
    this.companyId = this.$route.params.idCompany;
    this.load();
  },
  methods: {
    load() {
      this.loading = true;

      REQUEST_SERVICE.get(GET_TIMEZONE_LIST)
        .then((r) => { this.timezones = r.data || {}; })
        .catch(() => { this.timezones = {}; });

      REQUEST_SERVICE.requestContext(
        "GET",
        `${GET_WORKSHIFT_FULL_VIEW}?companyId=${this.companyId}`,
        this.$route.params.context
      ).then((response) => {
          const data = response.data || {};
          this.companyTimeZone = data.defaultTimeZone || "";
          this.workshifts = Array.isArray(data.workshifts) ? data.workshifts : [];
          // Pré-preenche os percentuais com o default da empresa (já vem como inteiro *100).
          this.form.firstPeriodTimePercent = data.firstPeriodTimePercent || 0;
          this.form.secondPeriodTimePercent = data.secondPeriodTimePercent || 0;
          this.form.thirdPeriodTimePercent = data.thirdPeriodTimePercent || 0;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          swal("Aviso", "Falha ao carregar turnos da empresa.", "error");
        });
    },
    goToCompanyPhaseRules() {
      this.$router.push({
        path: `/regras-horas-extras-empresa/${this.companyId}/${this.$route.params.context}`
      });
    },
    // Marca/desmarca apenas os turnos atualmente FILTRADOS, preservando a seleção
    // de turnos fora do filtro (união/subtração em vez de substituição).
    toggleAll(checked) {
      const filteredIds = this.filteredWorkshifts.map((w) => w.id);
      if (checked) {
        const set = {};
        this.selectedWorkshifts.forEach((id) => { set[id] = true; });
        filteredIds.forEach((id) => { set[id] = true; });
        this.selectedWorkshifts = Object.keys(set);
      } else {
        const remove = {};
        filteredIds.forEach((id) => { remove[id] = true; });
        this.selectedWorkshifts = this.selectedWorkshifts.filter((id) => !remove[id]);
      }
    },
    resetRules() {
      this.rules = DEFAULT_PHASE_TYPES.map(emptyRule);
    },
    loadTemplateFromWorkshift() {
      if (!this.templateWorkshiftId || this.loadingTemplate) return;
      this.loadingTemplate = true;

      REQUEST_SERVICE.requestContext(
        "GET",
        `${GET_WORKSHIFT_PHASES}/${this.templateWorkshiftId}/phases`,
        this.$route.params.context
      ).then((response) => {
        const incoming = Array.isArray(response.data) ? response.data : [];

        // Mescla as fases vindas do backend com o esqueleto de tipos
        // padrão pra garantir que a UI mostre todas as linhas mesmo
        // que algum tipo de dia não tenha registro no turno modelo.
        const byType = {};
        incoming.forEach((p) => {
          byType[p.workshiftDayPhaseType] = {
            workshiftDayPhaseType: p.workshiftDayPhaseType,
            isActive: !!p.isActive,
            firstPhaseDuration: normalizeTimeSpan(p.firstPhaseDuration),
            secondPhaseDuration: normalizeTimeSpan(p.secondPhaseDuration),
            thirdPhaseDuration: normalizeTimeSpan(p.thirdPhaseDuration),
            applyFirstPercentageToBalance: !!p.applyFirstPercentageToBalance,
            applySecondPercentageToBalance: !!p.applySecondPercentageToBalance,
            applyThirdPercentageToBalance: !!p.applyThirdPercentageToBalance,
            addIntervalToTotalPlus: !!p.addIntervalToTotalPlus
          };
        });

        this.rules = DEFAULT_PHASE_TYPES.map((t) => byType[t] || emptyRule(t));
        this.loadingTemplate = false;
        swal("Pronto", "Regras carregadas do turno modelo. Ajuste se precisar antes de aplicar.", "success");
      }).catch(() => {
        this.loadingTemplate = false;
        swal("Aviso", "Falha ao carregar regras do turno modelo.", "error");
      });
    },
    save() {
      if (this.saving || !this.canSave) return;
      this.saving = true;

      const payload = {
        companyId: this.companyId,
        workshiftIds: this.selectedWorkshifts,
        rules: this.rules.map((r) => ({
          workshiftDayPhaseType: r.workshiftDayPhaseType,
          isActive: r.isActive,
          firstPhaseDuration: r.firstPhaseDuration,
          secondPhaseDuration: r.secondPhaseDuration,
          thirdPhaseDuration: r.thirdPhaseDuration,
          applyFirstPercentageToBalance: r.applyFirstPercentageToBalance,
          applySecondPercentageToBalance: r.applySecondPercentageToBalance,
          applyThirdPercentageToBalance: r.applyThirdPercentageToBalance,
          addIntervalToTotalPlus: r.addIntervalToTotalPlus
        })),
        // Percentuais nível do turno (inteiros; backend divide por 100).
        applyPhasePercentages: this.form.applyPhasePercentages,
        firstPeriodTimePercent: this.form.firstPeriodTimePercent || 0,
        secondPeriodTimePercent: this.form.secondPeriodTimePercent || 0,
        thirdPeriodTimePercent: this.form.thirdPeriodTimePercent || 0
      };

      REQUEST_SERVICE.requestContext("PUT", APPLY_PHASE_RULES_BULK, this.$route.params.context, payload)
        .then(() => {
          this.saving = false;
          swal("Sucesso", "Regras aplicadas em massa.", "success");
        })
        .catch(() => {
          this.saving = false;
          swal("Aviso", "Falha ao aplicar regras. Verifique os campos e tente novamente.", "error");
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.configure-phase-rules {
  padding: 24px;

  .header {
    margin-bottom: 24px;
    h2 { font-weight: bold; margin-bottom: 8px; }
    p { color: #666; font-size: 13px; }
    .company-link-note {
      margin-top: 8px; padding: 8px 12px; background: #eef6fc;
      border-left: 3px solid #3598dc; border-radius: 4px;
      a { color: #3598dc; font-weight: 600; margin-left: 4px; }
    }
  }

  .loading-block { text-align: center; padding: 40px; color: #888; }

  .box {
    background: #fff;
    border-radius: 6px;
    padding: 18px;
    margin-bottom: 18px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);

    .box-title {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-weight: 600;
      margin-bottom: 12px;
      .select-all {
        font-weight: normal;
        font-size: 13px;
        color: #555;
        input { margin-right: 4px; }
      }
      .hint {
        display: inline-flex; align-items: center; justify-content: center;
        width: 18px; height: 18px; border-radius: 50%;
        background: #bbb; color: #fff; font-size: 11px; cursor: help;
        font-weight: bold;
      }
    }
  }

  /* Busca por nome na tabela de turnos */
  .workshift-search {
    display: flex; align-items: center; gap: 10px; margin-bottom: 10px; flex-wrap: wrap;
    input {
      flex: 1 1 260px; min-width: 200px;
      border: 1px solid #ccc; border-radius: 4px; padding: 6px 10px; font-size: 13px;
    }
    .count { font-size: 12px; color: #888; }
  }
  .badge-company {
    background: #f0ad4e; color: #fff; font-size: 10px; font-weight: 600;
    padding: 2px 7px; border-radius: 10px; white-space: nowrap;
  }
  .badge-own {
    background: #eee; color: #888; font-size: 10px; font-weight: 600;
    padding: 2px 7px; border-radius: 10px; white-space: nowrap;
  }
  .empty-row { text-align: center; color: #999; padding: 14px; font-style: italic; }

  /* Aviso: turnos que seguem a empresa não recebem o % individual */
  .same-config-warning {
    margin-top: 12px; padding: 10px 14px; font-size: 13px; line-height: 1.5;
    background: #fcf3e6; border-left: 4px solid #f0ad4e; border-radius: 4px; color: #7a5b1e;
    a { color: #3598dc; font-weight: 600; }
    .same-config-list { margin-top: 6px; font-size: 12px; color: #9a7a3a; font-style: italic; }
  }

  .template-box {
    background: #fefbef;
    border-left: 4px solid #f9ca04;
  }
  .template-row {
    display: flex; gap: 8px; align-items: center; flex-wrap: wrap;
    .template-search {
      flex: 1 1 220px; min-width: 180px;
      border: 1px solid #ccc; border-radius: 4px; padding: 6px 10px;
    }
    .template-select {
      flex: 1 1 320px; min-width: 240px;
      border: 1px solid #ccc; border-radius: 4px; padding: 6px 10px;
    }
    .btn-secondary {
      background: #f9ca04; color: #333;
      padding: 7px 18px; border-radius: 4px; border: none; cursor: pointer; font-weight: 600;
    }
    .btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
    .btn-link {
      background: none; border: none; color: #3598dc; cursor: pointer; font-size: 13px;
    }
  }
  .hint-line { font-size: 12px; color: #888; margin: 0 0 12px 0; }

  .checkbox-line label { display: flex; align-items: center; gap: 6px; font-weight: 500; font-size: 13px; }
  .percent-row {
    display: flex; gap: 16px; flex-wrap: wrap; margin-top: 10px;
    .percent-field {
      display: flex; flex-direction: column; gap: 4px;
      label { font-weight: 500; font-size: 13px; }
      input {
        border: 1px solid #ccc; border-radius: 4px; padding: 6px 10px; width: 120px;
      }
    }
  }

  .table-workshifts, .table-rules {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th, td {
      border-bottom: 1px solid #eee;
      padding: 8px 6px;
      text-align: left;
      vertical-align: middle;
    }
    th {
      color: #555; font-weight: 600; background: #fafafa;
      cursor: help; /* sinaliza que tem tooltip */
    }

    input[type="text"] {
      border: 1px solid #ccc;
      border-radius: 4px;
      padding: 4px 6px;
      width: 90px;
      font-family: monospace;
    }
  }

  .actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 20px;

    .btn-cancel, .btn-primary {
      border-radius: 4px;
      padding: 8px 24px;
      font-weight: 600;
      border: none;
      cursor: pointer;
    }
    .btn-cancel { background: #8b8787; color: #fff; }
    .btn-primary { background: #3598dc; color: #fff; }
    .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/companyDetail/configurePhaseRules.vue